import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// @mui components
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Custom components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Layout
import BasicLayout from 'layouts/authentication/components/BasicLayout';

// API
import {
  sendPasswordOTP,
  verifyPasswordOTP,
  resetPassword,
} from '../../../../api/axios';

// Image
import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import jcgnLogo from 'assets/images/jcgn-logo.png';

function ResetPassword() {
  const [step, setStep] = useState(1); // 1 = email, 2 = OTP, 3 = new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();

  const notify = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validEmail(email)) {
      notify('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordOTP(email);
      notify('OTP sent to your email.', 'success');
      setStep(2);
    } catch (error) {
      notify(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      notify('Please enter the 6-digit OTP.');
      return;
    }
    setLoading(true);
    try {
      await verifyPasswordOTP({ email, enteredOTP: otp });
      notify('OTP verified.', 'success');
      setStep(3);
    } catch (error) {
      notify(error.message || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      notify('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      notify('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ email, newPassword: password });
      notify('Password reset successful! You can sign in now.', 'success');
      setTimeout(() => navigate('/authentication/sign-in'), 2000);
    } catch (error) {
      notify(error.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordField = (label, value, setter) => (
    <MDBox mb={2}>
      <MDInput
        type={showPassword ? 'text' : 'password'}
        label={label}
        value={value}
        onChange={(e) => setter(e.target.value)}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </MDBox>
  );

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={{ xs: 2.5, sm: 3 }}
          mb={1}
          textAlign="center"
        >
          <MDBox
            component="img"
            src={jcgnLogo}
            alt="JCGN Logo"
            sx={{
              width: { xs: 120, sm: 140 },
              height: { xs: 120, sm: 140 },
              objectFit: 'contain',
              borderRadius: '12px',
              mb: 1.5,
            }}
          />
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Reset Password
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            {step === 1 && 'Enter your email to receive a verification code'}
            {step === 2 && `We sent a 6-digit code to ${email}`}
            {step === 3 && 'Enter your new password'}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {step === 1 && (
            <MDBox component="form" role="form" onSubmit={handleSendOtp}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                />
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Code'}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {step === 2 && (
            <MDBox component="form" role="form" onSubmit={handleVerifyOtp}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="OTP Code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  fullWidth
                />
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </MDButton>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" mt={2}>
                <MDTypography
                  variant="button"
                  color="info"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                  }}
                >
                  Change email
                </MDTypography>
                <MDTypography
                  variant="button"
                  color="info"
                  sx={{ cursor: 'pointer' }}
                  onClick={handleSendOtp}
                >
                  Resend code
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {step === 3 && (
            <MDBox component="form" role="form" onSubmit={handleResetPassword}>
              {passwordField('New Password', password, setPassword)}
              {passwordField('Confirm Password', confirmPassword, setConfirmPassword)}
              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          <MDBox mt={3} textAlign="center">
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Back to Sign In
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default ResetPassword;
